import React, { useState } from 'react';
import { useCookieConsent } from '../contexts/CookieConsentContext';
import { Cookie, Settings, Check, X as XIcon } from 'lucide-react';

const CookieConsentBanner: React.FC = () => {
  const {
    showBanner,
    consent,
    acceptAll,
    acceptNecessary,
    acceptAnalytics,
    rejectAnalytics
  } = useCookieConsent();

  const [showDetails, setShowDetails] = useState(false);

  if (!showBanner) return null;

  const handleAcceptAll = () => {
    acceptAll();
  };

  const handleAcceptNecessary = () => {
    acceptNecessary();
  };

  const handleToggleAnalytics = () => {
    if (consent?.analytics) {
      rejectAnalytics();
    } else {
      acceptAnalytics();
    }
  };

  const handleSavePreferences = () => {
    setShowDetails(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity" />

      {/* Banner */}
      <div className="relative w-full max-w-2xl bg-white dark:bg-slate-800 rounded-lg border-2 border-gray-200 dark:border-slate-700 transform transition-all scale-in">
        {!showDetails ? (
          // Main banner
          <div className="p-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-500 rounded-md flex items-center justify-center">
                  <Cookie className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                  Cookie Consent
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 leading-relaxed">
                  We use cookies to enhance your experience and analyze our traffic.
                  By clicking "Accept All", you consent to our use of cookies.
                  You can customize your preferences or accept only necessary cookies.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleAcceptAll}
                    className="px-6 py-2.5 bg-blue-500 text-white text-sm font-semibold rounded-md hover:bg-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 transition-all duration-200 hover:scale-105 active:scale-95"
                  >
                    Accept All
                  </button>
                  <button
                    onClick={handleAcceptNecessary}
                    className="px-6 py-2.5 bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-white text-sm font-medium rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 transition-all duration-200 border border-slate-200 dark:border-slate-600"
                  >
                    Necessary Only
                  </button>
                  <button
                    onClick={() => setShowDetails(true)}
                    className="px-6 py-2.5 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 text-sm font-medium rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 transition-all duration-200 flex items-center space-x-2"
                  >
                    <Settings className="h-4 w-4" />
                    <span>Customize</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Detailed preferences
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                  <Settings className="h-4 w-4 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  Cookie Preferences
                </h3>
              </div>
              <button
                onClick={() => setShowDetails(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors p-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <XIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Necessary Cookies */}
              <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-slate-900 dark:text-white mb-1">
                      Necessary Cookies
                    </h4>
                    <p className="text-xs text-slate-600 dark:text-slate-400 mb-2">
                      These cookies are essential for the website to function properly.
                      They cannot be disabled.
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <div className="w-8 h-4 bg-green-500 rounded-full flex items-center justify-end px-1">
                      <Check className="h-3 w-3 text-white" />
                    </div>
                    <span className="text-xs text-slate-600 dark:text-slate-400">Always Active</span>
                  </div>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-slate-900 dark:text-white mb-1">
                      Analytics Cookies
                    </h4>
                    <p className="text-xs text-slate-600 dark:text-slate-400 mb-2">
                      These cookies help us understand how visitors interact with our website
                      by collecting and reporting information anonymously.
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={handleToggleAnalytics}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        consent?.analytics ? 'bg-gradient-to-r from-blue-600 to-purple-600' : 'bg-slate-300 dark:bg-slate-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                          consent?.analytics ? 'translate-x-4' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                    <span className="text-xs text-slate-600 dark:text-slate-400">
                      {consent?.analytics ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
              <button
                onClick={() => setShowDetails(false)}
                className="px-6 py-2.5 text-slate-600 dark:text-slate-400 text-sm font-medium hover:text-slate-800 dark:hover:text-slate-200 transition-colors rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePreferences}
                className="px-6 py-2.5 bg-blue-500 text-white text-sm font-semibold rounded-md hover:bg-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 transition-all duration-200 hover:scale-105 active:scale-95"
              >
                Save Preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsentBanner;
